import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import Debug from 'debug'
import moize from 'moize'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import type { SortingState, TableState } from '@tanstack/react-table'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'

import { navigate } from '@jotta/router'
import { useBreakpointIndex } from '@jotta/ui/useBreakpointIndex'
import { cn } from '@jotta/utils/css'
import { sortBigint } from '@jotta/utils/number'

import { type ExtendedBusinessUser } from '../BizApi'

import { BizActionButtonDeleteUser } from './BizActionButtonDeleteUser'
import { BizActionButtonEditUser } from './BizActionButtonEditUser'
import { BizDisplayStorage } from './BizDisplayStorage'

const debug = Debug('jotta:biz:UserTable')

const columnHelper = createColumnHelper<ExtendedBusinessUser>()
const accessors = {
  name: columnHelper.accessor('name', {
    header: () => <Trans>Name</Trans>,
    cell: props => (
      <>
        <Link
          to={`/biz/users/${props.row.original.username}`}
          className="link"
          aria-label="Modify user"
        >
          {props.row.original.name || props.row.original.email}
          {props.row.original.isCurrentUser && (
            <span className="ml-1 text-gray-400">
              (<Trans>You</Trans>)
            </span>
          )}
        </Link>
      </>
    ),
  }),
  nameLink: columnHelper.accessor('name', {
    id: 'nameLink',
    sortingFn: (a, b) => {
      const name1 = a.original.name || a.original.email || ''
      const name2 = b.original.name || b.original.email || ''
      return name1.localeCompare(name2)
    },
    header: () => <Trans>Full name</Trans>,
    cell: props => (
      <>
        <Link
          to={`/biz/users/${props.row.original.username}`}
          className="link"
          aria-label="Modify user"
        >
          {props.row.original.name || props.row.original.email}
          {props.row.original.isCurrentUser && (
            <span className="ml-1 text-gray-400">
              (<Trans>You</Trans>)
            </span>
          )}
        </Link>
        <div className="body-sm-sh">{props.row.original.email}</div>
      </>
    ),
  }),
  role: columnHelper.accessor('admin', {
    id: 'role',
    sortingFn: (a, b) => {
      if (a.original.admin === b.original.admin) {
        return 0
      }
      if (a.original.admin) {
        return 1
      }
      return -1
    },
    header: () => <Trans>Role</Trans>,
    cell: props => (props.getValue() ? 'Admin' : 'User'),
  }),

  storage: columnHelper.accessor('storageBytes', {
    id: 'storage',
    sortingFn: (a, b) =>
      sortBigint(a.original.storageBytes, b.original.storageBytes),
    header: () => (
      <div className="whitespace-nowrap">
        <Trans>Total</Trans>
      </div>
    ),
    cell: props => {
      const { quotaBytes, storageBytes } = props.row.original
      return (
        <div className="whitespace-normal sm:whitespace-nowrap">
          <BizDisplayStorage
            storageBytes={storageBytes}
            quotaBytes={quotaBytes}
          />
        </div>
      )
    },
  }),
  lastWriteMillis: columnHelper.accessor('lastWriteMillis', {
    sortingFn: (a, b) =>
      sortBigint(a.original.lastWriteMillis, b.original.lastWriteMillis),
    sortUndefined: 'first',
    header: () => <Trans>Last upload</Trans>,
    cell: props => (
      <span className="whitespace-nowrap">
        {props.getValue()
          ? dayjs(Number(props.getValue())).fromNow()
          : t`Never uploaded`}
      </span>
    ),
  }),
  actions: columnHelper.display({
    header: () => <Trans>Action</Trans>,
    id: 'actions',
    size: 50,
    cell: props => {
      const user = props.row.original
      return (
        <div className="flex gap-1">
          <BizActionButtonEditUser
            className="btn btn-outline bg-white px-2"
            user={user}
          />
          <BizActionButtonDeleteUser
            className="btn btn-outline bg-white px-2"
            user={user}
          />
        </div>
      )
    },
  }),
}
const columns = [
  accessors.name,
  accessors.nameLink,
  accessors.role,
  accessors.storage,
  accessors.lastWriteMillis,
  accessors.actions,
]

type ColumnId = keyof typeof accessors
type ColumnVisibility = Partial<Record<ColumnId, boolean>>

const views = {
  full: {
    columnVisibility: {
      name: false,
      nameLink: true,
      lastWriteMillis: false,
    },
    columnVisibilityLarge: {
      name: false,
      nameLink: true,
    },
  },
  storage: {
    columnVisibility: {
      nameLink: false,
      role: false,
      lastWriteMillis: false,
      actions: false,
    },
    columnVisibilityLarge: undefined,
  },
  upload: {
    columnVisibility: {
      nameLink: false,
      role: false,
      storage: false,
      actions: false,
    },
    columnVisibilityLarge: undefined,
  },
} satisfies Record<
  string,
  {
    columnVisibility: ColumnVisibility
    columnVisibilityLarge: ColumnVisibility | undefined
  }
>
type TableView = keyof typeof views

const getSorting = moize(
  (sortBy: ColumnId = 'role', sortDescending = false): SortingState => {
    switch (sortBy) {
      case 'role':
        return [
          {
            id: 'role',
            desc: !sortDescending,
          },
          {
            id: 'nameLink',
            desc: sortDescending,
          },
        ]
      case 'name':
        return [
          {
            id: 'nameLink',
            desc: sortDescending,
          },
        ]
      default:
        return [
          {
            id: sortBy,
            desc: sortDescending,
          },
        ]
    }
  },
  {
    maxSize: 100,
    // onCacheHit: () => debug('Sorting Cache hit'),
    // onCacheAdd: () => debug('Sorting Cache addd'),
  },
)
const getState = moize(
  (
    view: TableView,
    breakpoint: number,
    sorting: SortingState,
  ): Partial<TableState> => {
    return {
      sorting,
      columnVisibility:
        breakpoint > 1
          ? views[view]?.columnVisibilityLarge || views[view].columnVisibility
          : views[view].columnVisibility,
    }
  },
  {
    maxSize: 100,
    // onCacheHit: () => debug('state Cache hit'),
    // onCacheAdd: () => debug('state Cache add'),
  },
)

export function BizUserTable({
  users,
  view = 'full',
  compact,
  sortBy = 'role',
  sortDescending = false,
}: {
  users: ExtendedBusinessUser[]
  view?: TableView
  compact?: boolean
  sortBy?: ColumnId
  sortDescending?: boolean
}) {
  const br = useBreakpointIndex()
  const state = getState(view, br, getSorting(sortBy, sortDescending))

  // debug('%s %s Column visibility2', view, br, state)
  const table = useReactTable({
    state,
    columns,
    data: users,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  // const navigate = useNavigate()
  return (
    <table
      className={cn('table table-zebra table-px-0', {
        'table-compact': compact,
        'table-py-1 sm:table-py-2': !compact,
      })}
    >
      <thead>
        {table.getHeaderGroups().map(headerGroup => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map(header =>
              header.isPlaceholder ? null : (
                <th
                  key={header.id}
                  style={{
                    width: header.id.startsWith('name')
                      ? 'auto'
                      : `${header.getSize()}px`,
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </th>
              ),
            )}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map(row => (
          <tr
            key={row.id}
            className="hover relative cursor-pointer"
            data-user={row.original.username}
            onClick={e => {
              const target = e.target
              if (
                target instanceof HTMLElement &&
                !target.closest('a, button')
              ) {
                const user = target.closest('tr')?.dataset.user
                if (user) {
                  e.preventDefault()
                  debug('Click row', user)
                  navigate(`/biz/users/${user}`)
                }
              }
              debug('Click row skipped')
            }}
          >
            {row.getVisibleCells().map(cell => (
              <td key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}
